import React, { useState } from 'react';
import { List, ListSubheader, Icon, Button, Tooltip, Select, MenuItem } from '@material-ui/core';
import ScoreIcon from 'global/components/ScoreIcon';
import ChecklistScores, { ChecklistScoresPrint } from '../ChecklistScores';
import useStyles from './styles';
import { get, last, size } from 'global/lib/lodash';
import clsx from 'clsx';
import { getItemKey, getId } from './utils';
import Modal from 'global/components/modal';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useScreen } from 'global/styles';
import ImageGrid from 'global/components/image-grid';
import colors from 'global/styles/colors';
import { Button as Btn } from 'global/components/Button';
import { stopProp, _size } from 'global/utils';
import { Row } from 'global/components/row';
import ReportPhotos from '../ReportPhotos';
import withRouter from 'react-router-dom/withRouter';
import { useSupersets } from 'global/hooks/useSupersets';
import { Text } from 'global/components/text';

export const ImageModal = (props) => {
  const { superset_id, assessment } = props;

  const [state, setState] = useState({
    superset_id,
  });

  const { supersets } = useSupersets({ assessment });

  const { data, loading, error } = useRetrieveData(
    { url: '/realm-photos' },
    {
      params: {
        filter: {
          superset_id: state.superset_id,
          assessment_id: get(assessment, 'id'),
        },
        include: 'supersets',
      },
      build: ['realmPhotos'],
      useEffect: true,
      transformData: (da) => {
        if (!state.superset_id) {
          return da.filter((d) => !size(d.supersets));
        }
        return da;
      },
    }
  );

  if (error) {
    return <div style={{ color: colors.redIcon }}>{error}</div>;
  }

  return (
    <div>
      <Select displayEmpty style={{ width: '100%' }} value={state.superset_id} onChange={(e) => setState((s) => ({ ...s, superset_id: e.target.value }))}>
        {[{ name: 'None', value: null }, ...supersets].map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name}
          </MenuItem>
        ))}
      </Select>
      <ImageGrid
        folders={data}
        includeSearch={false}
        loading={loading}
        userCanEdit={false}
        hideSuperset
        blankContent={() => <Text>There are no photos here; try changing the filters.</Text>}
        {...props}
      />
    </div>
  );
};

export const ImageSelectModal = withRouter((props) => {
  const [selected, setSelected] = useState({});

  const onSelectedChange = (sel) => {
    setSelected(
      Object.keys(sel)
        .filter((k) => !!sel[k])
        .map((k) => sel[k])
    );
  };

  const onClose = () => {
    props.onClose();
    setSelected({});
  };

  const onSubmit = () => {
    props.onSubmit && props.onSubmit({ images: selected });
    onClose();
  };

  const renderBtn = () => {
    if (!_size(selected)) {
      return { title: 'Add', disabled: true };
    }

    return {
      title: `Add ${_size(selected)}`,
      onClick: onSubmit,
    };
  };

  const IMAGEMODAL = props.useFullPhotos ? ReportPhotos : ImageModal;

  return (
    <Modal {...props} onClose={onClose} buttons={[renderBtn()]}>
      <IMAGEMODAL canSelect={true} {...props} _location={{ pathname: 'reports/photo' }} onSelectedChange={onSelectedChange} />
    </Modal>
  );
});

export const ImageSelectBtn = (props) => {
  const [modal, setModal] = useState({ open: false });

  const showImages = (e) => {
    stopProp(e);
    props.onOpen && props.onOpen();
    setModal({
      ...props,
      open: true,
      fullScreen: true,
      title: props.modalTitle || `Photos`,
      maxWidth: 920,
      onClose: () => {
        setModal((m) => ({ ...m, open: false }));
        props.onClose && props.onClose();
      },
    });
  };

  return (
    <>
      <Tooltip title={props.tooltip ? props.tooltip : props.disabled ? 'Please select a system to access your photos' : 'Insert from media already uploaded to the Photos tab'}>
        <span>
          <Btn {...props} onClick={showImages}>
            <Icon>photo_library</Icon>
          </Btn>
        </span>
      </Tooltip>
      <ImageSelectModal {...modal} />
    </>
  );
};

export function SystemScore(props) {
  const { system, setSidebarOpen, activeEdit, checklist, selectItem, selectedItems, showSelectBoxes, isCourse, notFirst, printView } = props;
  const classes = useStyles({ isCourse });
  const key = 'scorecardScoreChecklist';
  const pChildren = get(system, 'children', []).filter((f) => (activeEdit ? getId(checklist) === getId(f) : true));
  const { isMobileXS } = useScreen();

  const isSelected = (item) => get(selectedItems, getItemKey(item));
  const hasSelectedItems = Object.keys(selectedItems).filter((k) => get(selectedItems, [k, 'itemType']) === 'checkpoint').length > 0;

  const currentSelect = isSelected(system);
  const indeterminate = Object.keys(selectedItems).filter((k) => get(selectedItems, [k, 'parentId']) == system.id).length > 0;

  const ScoreIconProps = {
    ...(!activeEdit
      ? {
          useBlankCheckbox: showSelectBoxes,
          selected: showSelectBoxes && get(currentSelect, 'fullCount'),
          indeterminate: showSelectBoxes && indeterminate,
          onClick: () => selectItem(system),
        }
      : {}),

    className: clsx(classes.headerChild),
    score: get(system, 'value'),
  };

  return (
    <div className={notFirst && classes.system} key={getId(system)}>
      {!isCourse && (
        <ListSubheader className={classes.systemHeader} disableGutters={true}>
          <h2 className={clsx(classes.headerChild, classes.headerChildScore)}>{get(system, 'displayScore')}</h2>
          <ScoreIcon {...ScoreIconProps} />
          <h2 className={classes.headerChild}>
            <span className={clsx(classes.headerChild, classes.headerChildName)}>{get(system, 'name')}</span>
            {get(system, 'criteria') && <span className={classes.headerCriteria}>{get(system, 'criteria')}</span>}
          </h2>
          <div style={{ flexGrow: 1 }}></div>
          <Button className={classes.toggleSidebar} onClick={() => setSidebarOpen(true)}>
            <Icon>view_list</Icon>
          </Button>

          {!isMobileXS && !printView && (
            <ImageSelectBtn
              variant={'default'}
              tooltip={`${get(system, 'name')} Photos`}
              className={classes.showImages}
              superset_id={get(system, 'supersetId')}
              assessment={get(props, 'assessment.data')}
              canSelect={false}
            />
          )}
        </ListSubheader>
      )}
      <List>
        {pChildren.map((data, i) => {
          if (!printView) {
            return (
              <ChecklistScores
                key={`${key}_${i}`}
                {...props}
                checklist={data}
                isSelected={isSelected}
                hasSelectedItems={hasSelectedItems}
                next={(activeEdit && props.next) || get(pChildren, i + 1, get(props, 'next.children.0'))}
                prev={(activeEdit && props.prev) || get(pChildren, i - 1, last(get(props, 'prev.children')))}
              />
            );
          } else {
            return <ChecklistScoresPrint key={`${key}_${i}`} {...props} notFirst={i !== 0} checklist={data} />;
          }
        })}
      </List>
    </div>
  );
}
